import { API_BASE_URL } from '@/constants';
import type {
  AdminProfile,
  UpdateProfileDto,
  ApiProfileResponse,
  ApiUpdateProfileResponse,
} from './profile.dto';

import { redirectToLogin } from '../../utils/sessionExpired';
const customLog = (...args: unknown[]) => {
  console.log(...args);
};

class ProfileService {
  private baseUrl: string;
  private logoutCallback: (() => void) | null = null;

  constructor() {
    this.baseUrl = API_BASE_URL;
  }

  setLogoutCallback(callback: () => void) {
    this.logoutCallback = callback;
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;
    customLog('=== API Request ===');
    customLog('URL:', url);
    customLog('Method:', options.method || 'GET');

    const response = await fetch(url, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
    });

    // Check for 401 Unauthorized
    if (response.status === 401) {
      customLog('Session expired - triggering logout');
      if (this.logoutCallback) {
        this.logoutCallback();
      }
      redirectToLogin();
      throw new Error('Session expired. Please login again.');
    }

    if (!response.ok) {
      try {
        const errorData = await response.json();
        throw new Error(
          errorData.message || `HTTP error! status: ${response.status}`
        );
      } catch (e) {
        if (
          e instanceof Error &&
          e.message !== `HTTP error! status: ${response.status}`
        ) {
          throw e;
        }
      }

      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    customLog('Response data:', data);
    return data;
  }

  async getProfile(token: string): Promise<AdminProfile> {
    customLog('API Service: getProfile called');

    const response = await this.request<ApiProfileResponse>('/admin/profile', {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.data) {
      customLog('API Service: Invalid response structure:', response);
      throw new Error('Invalid response format from server');
    }

    return response.data;
  }

  async updateProfile(
    token: string,
    profileData: UpdateProfileDto
  ): Promise<AdminProfile> {
    customLog('API Service: updateProfile called with data:', profileData);

    const response = await this.request<ApiUpdateProfileResponse>(
      '/admin/profile',
      {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(profileData),
      }
    );

    if (!response.data) {
      customLog('API Service: Invalid response structure:', response);
      throw new Error('Invalid response format from server');
    }

    return response.data;
  }
}

export const profileService = new ProfileService();
